import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { Box, Typography, TextField, Stack, Switch, FormControlLabel } from '@mui/material'
import * as yup from 'yup'
import { Button, Spinner } from '../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../constants/colors'
import {
  DEEPER_TEAM_ANALYSIS_EXPERIENCE_KEY,
  DEEPER_INDIVIDUAL_ANALYSIS_EXPERIENCE_KEY
} from '../../gamer/constants/storageKeys'
import { getRoleFromPath } from '../../../config/routes/routeEnums'
import { GamerRoutes, FacilitatorRoutes } from '../../../config/routes/routeEnums'
import { useClearQuestionnaire, useUpdateUser } from '../../../apiLayer'
import useAppState from '../../../hooks/useAppState'
import { useFeatureFlags } from '../../../shared/hooks/useFeatureFlags'
import { useAppDispatch } from '../../../store'
import { updateFeatureFlag } from '../../../store/slices/uiSlice'
import { triggerTestNotification, clearNotifiedActivities } from '../../../store/slices/activitiesSlice'
import { addNotification } from '../../../store/slices/inAppNotificationsSlice'
import { ActivityType } from '../../../../../pure-js/enums/ActivityType'
import { InAppNotificationType } from '../../../../../pure-js/enums/InAppNotificationType'
import type { InAppNotification } from '../../../../../pure-js/types/InAppNotification'
import {
  DEFAULT_GLINT_DURATION_CONFIG,
  loadGlintDurationConfig,
  persistGlintDurationConfig
} from '../../feedbackGenerator/constants/glintConfig'
import { ASSESSMENT_TOTAL_QUESTIONS, deriveAssessmentAnsweredCount } from '../utils/onboardingProgress'
import { loadDevModeFromStorage, saveDevModeToStorage } from '../../../shared/libs/devModePersistence'
import { callFunction } from '../../../libs/CloudFunctionsApiHandlerHelper'
import { CloudFunction } from '../../../../../pure-js/enums/CloudFunction.js'
import { isPlatformSuperAdmin } from '../../../../../pure-js/libs/isSuperUser'
import type { WherebyLiveTranscriptionEvent } from '../../../../../pure-js/types/WherebyLiveTranscriptionEvent.js'
import type {
  UpsertWherebyTaskFromEventRequest,
  UpsertWherebyTaskFromEventResponse
} from '../../../../../pure-js/types/WherebyRerun.js'

const wherebyEventSchema: yup.ObjectSchema<WherebyLiveTranscriptionEvent> = yup
  .object({
    apiVersion: yup.string().oneOf(['1.0']).required('apiVersion is required'),
    id: yup.string().required('id is required'),
    createdAt: yup.string().required('createdAt is required'),
    type: yup.mixed<'transcription.finished'>().oneOf(['transcription.finished']).required('type is required'),
    data: yup
      .object({
        transcriptionId: yup.string().required('data.transcriptionId is required'),
        roomName: yup.string().required('data.roomName is required'),
        roomSessionId: yup.string().required('data.roomSessionId is required'),
        type: yup.mixed<'LIVE_TRANSCRIPTION'>().oneOf(['LIVE_TRANSCRIPTION']).required('data.type is required'),
        filename: yup.string().required('data.filename is required'),
        storageType: yup.mixed<'WHEREBY_HOSTED'>().oneOf(['WHEREBY_HOSTED']).required('data.storageType is required'),
        durationInSeconds: yup
          .number()
          .typeError('data.durationInSeconds must be a number')
          .required('data.durationInSeconds is required')
      })
      .required('data is required')
  })
  .required()

/**
 * Shared Dev Tools page
 * Accessible by both gamer and facilitator roles
 * Uses role-aware navigation
 */
export const DevToolsShared: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { state, setState } = useAppState()
  const updateUserMutation = useUpdateUser()
  const clearQuestionnaireMutation = useClearQuestionnaire()
  const dispatch = useAppDispatch()
  const { isLatestSessionFeedbackEnabled, isGenerateReportsEnabled, isAchievementsEnabled } = useFeatureFlags()
  const [isLoadingFlags, setIsLoadingFlags] = React.useState(true)
  const [isDevModeEnabled, setIsDevModeEnabled] = React.useState(() => loadDevModeFromStorage())
  const [showI18nKeys, setShowI18nKeys] = React.useState(() => {
    if (typeof window === 'undefined') return false
    try {
      return localStorage.getItem('i18n-show-keys') === 'true'
    } catch {
      return false
    }
  })

  const [wherebyEventRaw, setWherebyEventRaw] = React.useState('')
  const [wherebyForce, setWherebyForce] = React.useState(true)
  const [wherebySubmitError, setWherebySubmitError] = React.useState<string | null>(null)
  const [wherebySubmitResult, setWherebySubmitResult] = React.useState<UpsertWherebyTaskFromEventResponse | null>(null)
  const [isSubmittingWhereby, setIsSubmittingWhereby] = React.useState(false)
  const onboardingContext = 'preassessment'

  const parseWherebyEventFromText = React.useCallback((raw: string) => {
    const trimmed = (raw || '').trim()
    if (!trimmed) return { ok: false as const, error: 'Paste an event JSON first.' }

    const normalized = trimmed.startsWith('onWhereByEvent:') ? trimmed.slice('onWhereByEvent:'.length).trim() : trimmed

    let parsed: unknown
    try {
      parsed = JSON.parse(normalized)
    } catch {
      return { ok: false as const, error: 'Invalid JSON. Tip: you can paste the whole “onWhereByEvent: {...}” line.' }
    }

    try {
      const event = wherebyEventSchema.validateSync(parsed, { abortEarly: true })
      return { ok: true as const, event }
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        return { ok: false as const, error: err.message }
      }
      return { ok: false as const, error: 'Validation failed for unknown reasons.' }
    }
  }, [])

  const wherebyParse = React.useMemo(
    () => parseWherebyEventFromText(wherebyEventRaw),
    [parseWherebyEventFromText, wherebyEventRaw]
  )

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // Persist dev mode state to localStorage when it changes
  React.useEffect(() => {
    saveDevModeToStorage(isDevModeEnabled)
  }, [isDevModeEnabled])

  // Persist i18n keys display state to localStorage when it changes
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      if (showI18nKeys) {
        localStorage.setItem('i18n-show-keys', 'true')
      } else {
        localStorage.removeItem('i18n-show-keys')
      }
      // Dispatch custom event to trigger immediate re-render in useTranslation hook
      window.dispatchEvent(new Event('i18n-keys-toggled'))
    } catch {
      // Ignore localStorage errors
    }
  }, [showI18nKeys])

  // Check if feature flags are loaded
  React.useEffect(() => {
    // Feature flags should be available immediately from Redux, but give a small delay for initial render
    const timer = setTimeout(() => {
      setIsLoadingFlags(false)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  const answeredQuestionsCount = React.useMemo(
    () =>
      deriveAssessmentAnsweredCount({
        currentQuestion: state.user?.currentAssessmentQuestion,
        completedSteps: state.user?.completedOnboardingSteps,
        totalQuestions: ASSESSMENT_TOTAL_QUESTIONS
      }),
    [state.user?.completedOnboardingSteps, state.user?.currentAssessmentQuestion]
  )

  const [glintDurationField, setGlintDurationField] = React.useState(
    DEFAULT_GLINT_DURATION_CONFIG.glintDuration.toString()
  )
  const [fadeDurationField, setFadeDurationField] = React.useState(
    DEFAULT_GLINT_DURATION_CONFIG.fadeDuration.toString()
  )
  const [experienceDurationField, setExperienceDurationField] = React.useState(
    DEFAULT_GLINT_DURATION_CONFIG.experienceDuration.toString()
  )

  // Detect current role from URL
  const role = getRoleFromPath(location.pathname) || 'gamer'

  // Helper to get role-aware route for dev-tools (now role-specific)
  const getRoute = (routeKey: string) => {
    const base = role === 'gamer' ? GamerRoutes.BASE : FacilitatorRoutes.BASE
    // Map route keys to actual routes
    const routeMap: Record<string, string> = {
      DEV_TOOLS_VOICE_LOTTIE: '/dev-tools/voice-lottie',
      DEV_TOOLS_BUBBLE_LOTTIE: '/dev-tools/bubble-lottie',
      DEV_TOOLS_VOICE_ANIMATION_AUDIO: '/dev-tools/voice-animation-audio',
      DEV_TOOLS_RECHARTS_GLASS: '/dev-tools/recharts-glass',
      DEV_TOOLS_GLINT_REVEAL: '/dev-tools/glint-reveal',
      DEV_TOOLS_GENERATE_REPORT_VISUALS: '/dev-tools/generate-report-visuals',
      DEV_TOOLS_COMMUNICATION_FEELING: '/dev-tools/communication-feeling',
      DEV_TOOLS_STACKED_CHARTS: '/dev-tools/stacked-charts',
      DEV_TOOLS_FUNNEL_GRAPH: '/dev-tools/funnel-graph',
      DEV_TOOLS_FUNNEL_GRAPH_V2: '/dev-tools/funnel-graph-v2',
      DEV_TOOLS_FUNNEL_GRAPH_V3: '/dev-tools/funnel-graph-v3',
      DEV_TOOLS_COMPARISON_BAR: '/dev-tools/comparison-bar',
      DEV_TOOLS_TEAM_HEALTH_HEART: '/dev-tools/team-health-heart',
      DEV_TOOLS_TEAM_INFORMATION_SHARES: '/dev-tools/team-information-shares',
      DEV_TOOLS_ACHIEVEMENTS: '/dev-tools/achievements',
      DEV_TOOLS_PERSONALITY_TRAITS: '/dev-tools/personality-traits',
      DEV_TOOLS_BO_CLEANUP: '/dev-tools/bo-cleanup'
    }
    return `${base}${routeMap[routeKey] || '/dev-tools'}`
  }

  React.useEffect(() => {
    const stored = loadGlintDurationConfig()
    setGlintDurationField(stored.glintDuration.toString())
    setFadeDurationField(stored.fadeDuration.toString())
    setExperienceDurationField(stored.experienceDuration.toString())
  }, [])

  const parseDurationInput = React.useCallback((value: string, fallback: number) => {
    const parsed = Number(value)
    if (!Number.isFinite(parsed)) return fallback
    return Math.max(0, parsed)
  }, [])

  const handleBack = () => {
    navigate(-1)
  }

  const handleApplyGlintDurations = React.useCallback(() => {
    const config = {
      glintDuration: parseDurationInput(glintDurationField, DEFAULT_GLINT_DURATION_CONFIG.glintDuration),
      fadeDuration: parseDurationInput(fadeDurationField, DEFAULT_GLINT_DURATION_CONFIG.fadeDuration),
      experienceDuration: parseDurationInput(experienceDurationField, DEFAULT_GLINT_DURATION_CONFIG.experienceDuration)
    }

    persistGlintDurationConfig(config)
    const sanitized = loadGlintDurationConfig()
    setGlintDurationField(sanitized.glintDuration.toString())
    setFadeDurationField(sanitized.fadeDuration.toString())
    setExperienceDurationField(sanitized.experienceDuration.toString())
    alert('Glint durations saved. Restart the animation to preview the new timings.')
  }, [experienceDurationField, fadeDurationField, glintDurationField, parseDurationInput])

  const handleResetGlintDurations = React.useCallback(() => {
    persistGlintDurationConfig(DEFAULT_GLINT_DURATION_CONFIG)
    setGlintDurationField(DEFAULT_GLINT_DURATION_CONFIG.glintDuration.toString())
    setFadeDurationField(DEFAULT_GLINT_DURATION_CONFIG.fadeDuration.toString())
    setExperienceDurationField(DEFAULT_GLINT_DURATION_CONFIG.experienceDuration.toString())
    alert('Glint durations reset to defaults.')
  }, [])

  const handleVoiceLottieTest = () => {
    navigate(getRoute('DEV_TOOLS_VOICE_LOTTIE'))
  }

  const handleBubbleLottieTest = () => {
    navigate(getRoute('DEV_TOOLS_BUBBLE_LOTTIE'))
  }

  const handleVoiceAnimationWithAudio = () => {
    navigate(getRoute('DEV_TOOLS_VOICE_ANIMATION_AUDIO'))
  }

  const handleRechartsGlassTest = () => {
    navigate(getRoute('DEV_TOOLS_RECHARTS_GLASS'))
  }

  const handleGlintRevealTest = () => {
    navigate(getRoute('DEV_TOOLS_GLINT_REVEAL'))
  }

  const handleGenerateReportVisuals = () => {
    navigate(getRoute('DEV_TOOLS_GENERATE_REPORT_VISUALS'))
  }

  const handleClearUserProfile = async () => {
    try {
      const clearedUser = {
        ...state.user,
        displayName: '',
        phone: '',
        privacyPolicyAgree: false
      }

      await updateUserMutation.mutateAsync({
        id: state.user.id,
        userData: {
          displayName: '',
          phone: '',
          privacyPolicyAgree: false
        }
      })

      setState({
        ...state,
        user: clearedUser
      })

      alert('User profile cleared! You can now test the onboarding flow.')
    } catch (error) {
      alert('Error clearing user profile')
    }
  }

  const handleClearOnboardingProgress = async () => {
    try {
      const clearedUser = {
        ...state.user,
        completedOnboardingSteps: [],
        currentAssessmentQuestion: undefined
      }

      await clearQuestionnaireMutation.mutateAsync({ userId: state.user.id, context: onboardingContext })

      await updateUserMutation.mutateAsync({
        id: state.user.id,
        userData: {
          completedOnboardingSteps: [],
          currentAssessmentQuestion: undefined
        }
      })

      setState({
        ...state,
        user: clearedUser
      })

      alert('Onboarding progress cleared! You can now test the onboarding flow.')
    } catch (error) {
      alert('Error clearing onboarding progress')
    }
  }

  const handleCommunicationFeelingExamples = () => {
    navigate(getRoute('DEV_TOOLS_COMMUNICATION_FEELING'))
  }

  const handleStackedChartsTest = () => {
    navigate(getRoute('DEV_TOOLS_STACKED_CHARTS'))
  }

  const handleFunnelGraphTest = () => {
    navigate(getRoute('DEV_TOOLS_FUNNEL_GRAPH'))
  }

  const handleFunnelGraphTestV2 = () => {
    navigate(getRoute('DEV_TOOLS_FUNNEL_GRAPH_V2'))
  }

  const handleFunnelGraphTestV3 = () => {
    navigate(getRoute('DEV_TOOLS_FUNNEL_GRAPH_V3'))
  }

  const handleComparisonBarTest = () => {
    navigate(getRoute('DEV_TOOLS_COMPARISON_BAR'))
  }

  const handleTeamHealthHeartTest = () => {
    navigate(getRoute('DEV_TOOLS_TEAM_HEALTH_HEART'))
  }

  const handleTeamInformationSharesTest = () => {
    navigate(getRoute('DEV_TOOLS_TEAM_INFORMATION_SHARES'))
  }

  const handlePersonalityTraitsTest = () => {
    navigate(getRoute('DEV_TOOLS_PERSONALITY_TRAITS'))
  }

  const handleBOCleanup = () => {
    navigate(getRoute('DEV_TOOLS_BO_CLEANUP'))
  }

  const handleUnseenAchievements = () => {
    navigate(GamerRoutes.UNSEEN_ACHIEVEMENTS)
  }

  const handleAICoach = () => {
    navigate(GamerRoutes.AI_COACH)
  }

  const handleResetDeeperTeamAnalysisExperience = () => {
    try {
      if (typeof window === 'undefined') return
      window.localStorage.removeItem(DEEPER_TEAM_ANALYSIS_EXPERIENCE_KEY)

      alert('Deeper Team Analysis glow experience reset. Reload the page to see it again.')
    } catch (error) {
      alert('Error resetting the Deeper Team Analysis experience')
    }
  }

  const handleResetDeeperIndividualAnalysisExperience = () => {
    try {
      if (typeof window === 'undefined') return
      window.localStorage.removeItem(DEEPER_INDIVIDUAL_ANALYSIS_EXPERIENCE_KEY)
      alert('Deeper Individual Analysis glow experience reset. Reload the page to see it again.')
    } catch (error) {
      alert('Error resetting the Deeper Individual Analysis experience')
    }
  }

  // Marks no game as read, so Latest Session Feedback and My Profile are covered and "New" again.
  const handleResetFeedbackReadMarks = async () => {
    try {
      const at = new Date().toISOString()
      await updateUserMutation.mutateAsync({
        id: state.user.id,
        userData: { tasks: { latestSessionFeedback: { sessionId: '', at }, profileFeedback: { sessionIds: [], at } } }
      })
      alert('Feedback read marks reset. Reload the page to see the covered feedback again.')
    } catch (error) {
      alert('Error resetting the feedback read marks')
    }
  }

  const handleResetAllExperienceKeys = () => {
    try {
      if (typeof window === 'undefined') return
      window.localStorage.removeItem(DEEPER_TEAM_ANALYSIS_EXPERIENCE_KEY)
      window.localStorage.removeItem(DEEPER_INDIVIDUAL_ANALYSIS_EXPERIENCE_KEY)
      alert('All glow experience keys reset. Reload the pages to see the first-visit experiences again.')
    } catch (error) {
      alert('Error resetting all experience keys')
    }
  }

  const handleResetToSignUp = async () => {
    try {
      if (!state.user.id || state.user.id.trim() === '') {
        alert('Error: User not authenticated. Please log in first.')
        return
      }

      await updateUserMutation.mutateAsync({
        id: state.user.id,
        userData: {
          displayName: '',
          phone: '',
          privacyPolicyAgree: false
        }
      })

      setState({
        ...state,
        user: {
          ...state.user,
          displayName: '',
          phone: '',
          privacyPolicyAgree: false
        }
      })

      navigate('/signed-in?step=SIGN_UP_FORM')
    } catch (error) {
      alert('Error resetting to sign-up')
    }
  }

  const handleToggleDevMode = () => {
    setIsDevModeEnabled((prev) => !prev)
  }

  const handleSubmitWherebyBackfill = async () => {
    setWherebySubmitError(null)
    setWherebySubmitResult(null)

    if (!wherebyParse.ok) {
      setWherebySubmitError(wherebyParse.error)
      return
    }

    setIsSubmittingWhereby(true)
    try {
      const payload: UpsertWherebyTaskFromEventRequest = { event: wherebyParse.event, force: wherebyForce }
      const result = await callFunction<UpsertWherebyTaskFromEventRequest, UpsertWherebyTaskFromEventResponse>(
        CloudFunction.upsertWherebyTaskFromEvent,
        state,
        payload
      )
      setWherebySubmitResult(result)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setWherebySubmitError(message)
    } finally {
      setIsSubmittingWhereby(false)
    }
  }

  return (
    <Box sx={{ padding: '24px', backgroundColor: INTERAX_COLORS.offBlack, minHeight: '100vh' }}>
      {/* Header with back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <Button variant="secondary" onClick={handleBack} icon={<ArrowLeftIcon className="w-4 h-4" />} />
        <Box className="text-headline-1" sx={{ color: INTERAX_COLORS.white }}>
          Dev Tools ({role})
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: '800px' }}>
        {/* Dev Mode Control Section - Always First */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '48px' }}>
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              🔧 Dev Mode
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Enable or disable dev mode. State persists across page refreshes.
            </Typography>
            {isDevModeEnabled && (
              <Box
                sx={{
                  padding: '12px',
                  marginBottom: '16px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  border: `1px solid rgba(76, 175, 80, 0.5)`
                }}
              >
                <Typography sx={{ color: INTERAX_COLORS.white, fontSize: '14px', fontWeight: 500 }}>
                  ✓ Dev Mode Active
                </Typography>
              </Box>
            )}
            <Button variant={isDevModeEnabled ? 'primary' : 'secondary'} onClick={handleToggleDevMode}>
              {isDevModeEnabled ? 'Disable Dev Mode' : 'Enable Dev Mode'}
            </Button>

            {/* AI Coach Navigation Buttons */}
            <Box sx={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Button variant="secondary" onClick={() => navigate(GamerRoutes.AI_COACH)}>
                Open AI Coach (Normal)
              </Button>
              <Button variant="secondary" onClick={() => navigate(`${GamerRoutes.AI_COACH}?enabledDevAICoach=true`)}>
                Open AI Coach (Dev Mode)
              </Button>
            </Box>

            {/* Show i18n Translation Keys Toggle */}
            <Box sx={{ marginTop: '24px', paddingTop: '24px', borderTop: `1px solid ${INTERAX_COLORS.heritageBlue}` }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showI18nKeys}
                    onChange={(e) => setShowI18nKeys(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: INTERAX_COLORS.sun
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: INTERAX_COLORS.sun
                      }
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: INTERAX_COLORS.white,
                      fontFamily: 'Lexend Deca'
                    }}
                  >
                    Show i18n Translation Keys
                  </Typography>
                }
                sx={{
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    marginLeft: '8px'
                  }
                }}
              />
              <Box sx={{ marginTop: '12px' }}>
                {/* TODO: Change to https://intraction.com when public site domain is live */}
                <Button
                  variant="secondary"
                  onClick={() => window.open('https://intraction-website.web.app/', '_blank', 'noopener,noreferrer')}
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start'
                  }}
                >
                  View Public Homepage
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Activity Notifications Testing Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '48px' }}>
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              🔔 Activity Notifications Testing
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '8px' }}>
              The notification system is now BE-driven. Notifications are created in Firestore and synced via onSnapshot
              listener. Dev buttons below work for UI testing via Redux state.
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Test the activity notification system. Notifications appear in the top-right corner.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Button
                variant="primary"
                onClick={() => {
                  dispatch(triggerTestNotification())
                }}
              >
                Trigger activity notification (3 min before session)
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const scheduledStartTime = new Date(Date.now() + 3 * 60 * 1000).toISOString()
                  const devNotification: InAppNotification = {
                    id: `dev_ai_debrief_${Date.now()}`,
                    userId: state.user?.id || 'dev',
                    type: InAppNotificationType.GAME_REMINDER,
                    createdAt: new Date().toISOString(),
                    payload: {
                      activityId: `dev-ai-debrief-${Date.now()}`,
                      activityType: ActivityType.SHORT_REFLECTION,
                      sprintId: 'dev-sprint',
                      sprintTitle: 'Dev Test Sprint',
                      scheduledStartTime
                    }
                  }
                  dispatch(addNotification(devNotification))
                }}
              >
                Trigger AI debrief reminder (short)
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const scheduledStartTime = new Date(Date.now() + 3 * 60 * 1000).toISOString()
                  const devNotification: InAppNotification = {
                    id: `dev_ai_debrief_long_${Date.now()}`,
                    userId: state.user?.id || 'dev',
                    type: InAppNotificationType.GAME_REMINDER,
                    createdAt: new Date().toISOString(),
                    payload: {
                      activityId: `dev-ai-debrief-long-${Date.now()}`,
                      activityType: ActivityType.LONG_REFLECTION,
                      sprintId: 'dev-sprint',
                      sprintTitle: 'Dev Test Sprint',
                      scheduledStartTime
                    }
                  }
                  dispatch(addNotification(devNotification))
                }}
              >
                Trigger AI debrief reminder (long)
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const devNotification: InAppNotification = {
                    id: `dev_report_ready_${Date.now()}`,
                    userId: state.user?.id || 'dev',
                    type: InAppNotificationType.REPORT_READY,
                    createdAt: new Date().toISOString(),
                    payload: { reportId: `dev-report-${Date.now()}`, reportTitle: 'Dev Test Report' }
                  }
                  dispatch(addNotification(devNotification))
                }}
              >
                Trigger Report ready (facilitator)
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const devNotification: InAppNotification = {
                    id: `dev_feedback_ready_${Date.now()}`,
                    userId: state.user?.id || 'dev',
                    type: InAppNotificationType.FEEDBACK_READY,
                    createdAt: new Date().toISOString(),
                    payload: { sessionId: `dev-session-${Date.now()}`, sprintTitle: 'Dev Test Sprint' }
                  }
                  dispatch(addNotification(devNotification))
                }}
              >
                Trigger Feedback ready (gamer)
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  dispatch(clearNotifiedActivities())
                }}
              >
                Clear activity notification history
              </Button>
            </Box>
          </Box>
        </Box>

        {/* 
          IMPORTANT: Feature Flags section must always be the second content section after Dev Mode.
          New dev tools features should be added ABOVE this section, not below.
        */}
        {/* Feature Flags Section - Always Second */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '48px' }}>
          {/* Dev Settings - Feature Flags */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              🚩 Feature Flags
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Toggle feature flags for development and testing. Changes are persisted to localStorage.
            </Typography>
            {isLoadingFlags ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, padding: '12px' }}>
                <Spinner size="small" color="sun" />
                <Typography sx={{ color: INTERAX_COLORS.typographyFont, fontSize: '14px' }}>
                  Loading feature flags...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Latest Session Feedback Toggle */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: INTERAX_COLORS.white
                    }}
                  >
                    Latest Session Feedback
                  </Typography>
                  <Button
                    variant={isLatestSessionFeedbackEnabled ? 'primary' : 'secondary'}
                    onClick={() => {
                      dispatch(
                        updateFeatureFlag({
                          flag: 'enableLatestSessionFeedback',
                          value: !isLatestSessionFeedbackEnabled
                        })
                      )
                    }}
                  >
                    {isLatestSessionFeedbackEnabled ? 'Disable' : 'Enable'}
                  </Button>
                </Box>
                {/* Generate Reports Toggle */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: INTERAX_COLORS.white
                    }}
                  >
                    Generate Reports
                  </Typography>
                  <Button
                    variant={isGenerateReportsEnabled ? 'primary' : 'secondary'}
                    onClick={() => {
                      dispatch(updateFeatureFlag({ flag: 'enableGenerateReports', value: !isGenerateReportsEnabled }))
                    }}
                  >
                    {isGenerateReportsEnabled ? 'Disable' : 'Enable'}
                  </Button>
                </Box>
                {/* Achievements Toggle */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: INTERAX_COLORS.white
                    }}
                  >
                    Achievements
                  </Typography>
                  <Button
                    variant={isAchievementsEnabled ? 'primary' : 'secondary'}
                    onClick={() => {
                      dispatch(updateFeatureFlag({ flag: 'enableAchievements', value: !isAchievementsEnabled }))
                    }}
                  >
                    {isAchievementsEnabled ? 'Disable' : 'Enable'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Whereby task backfill (admin-only) */}
        {isPlatformSuperAdmin(state.user) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '48px' }}>
            <Box
              sx={{
                padding: '24px',
                backgroundColor: INTERAX_COLORS.heritageBlue40,
                borderRadius: '12px',
                border: `1px solid ${INTERAX_COLORS.heritageBlue}`
              }}
            >
              <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
                🎧 Whereby Task Backfill
              </Typography>
              <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
                Paste a `transcription.finished` event JSON (or the full `onWhereByEvent: ...` line). This will validate
                it and call the backend to upsert/requeue the task.
              </Typography>

              <Stack spacing={2}>
                <TextField
                  value={wherebyEventRaw}
                  onChange={(e) => {
                    setWherebyEventRaw(e.target.value)
                    setWherebySubmitError(null)
                    setWherebySubmitResult(null)
                  }}
                  placeholder="onWhereByEvent: { ... }"
                  multiline
                  minRows={6}
                  fullWidth
                  InputProps={{
                    sx: {
                      color: INTERAX_COLORS.white,
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: INTERAX_COLORS.heritageBlue },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: INTERAX_COLORS.sun },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: INTERAX_COLORS.sun }
                    }
                  }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={wherebyForce}
                      onChange={(e) => setWherebyForce(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: INTERAX_COLORS.sun },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: INTERAX_COLORS.sun
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '14px', color: INTERAX_COLORS.white, fontFamily: 'Lexend Deca' }}>
                      Force requeue (allows resetting completed tasks)
                    </Typography>
                  }
                  sx={{ margin: 0 }}
                />

                <Box
                  sx={{
                    padding: '12px',
                    borderRadius: '8px',
                    backgroundColor: wherebyParse.ok ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 152, 0, 0.15)',
                    border: `1px solid ${wherebyParse.ok ? 'rgba(76, 175, 80, 0.5)' : 'rgba(255, 152, 0, 0.4)'}`
                  }}
                >
                  <Typography sx={{ color: INTERAX_COLORS.white, fontSize: '14px', fontWeight: 500 }}>
                    {wherebyParse.ok ? `✓ Valid event (taskId: ${wherebyParse.event.id})` : `⚠ ${wherebyParse.error}`}
                  </Typography>
                </Box>

                {wherebySubmitError && (
                  <Box
                    sx={{
                      padding: '12px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(244, 67, 54, 0.2)',
                      border: '1px solid rgba(244, 67, 54, 0.5)'
                    }}
                  >
                    <Typography sx={{ color: INTERAX_COLORS.white, fontSize: '14px' }}>{wherebySubmitError}</Typography>
                  </Box>
                )}

                {wherebySubmitResult && (
                  <Box
                    sx={{
                      padding: '12px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(76, 175, 80, 0.2)',
                      border: '1px solid rgba(76, 175, 80, 0.5)'
                    }}
                  >
                    <Typography sx={{ color: INTERAX_COLORS.white, fontSize: '14px' }}>
                      {`Queued: ${wherebySubmitResult.enqueued ? 'yes' : 'no'} | status: ${wherebySubmitResult.status} | taskId: ${wherebySubmitResult.taskId}`}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant={wherebyParse.ok ? 'primary' : 'secondary'}
                    disabled={!wherebyParse.ok || isSubmittingWhereby}
                    onClick={handleSubmitWherebyBackfill}
                  >
                    {isSubmittingWhereby ? 'Submitting…' : 'Upsert & Requeue'}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Box>
        )}

        {/* BO Clean Up */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', marginBottom: '48px' }}>
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.redErrorBackground,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.redError}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              🧹 BO Clean Up
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Clean up the back office with the Delete BE button
            </Typography>
            <Button variant="primary" onClick={handleBOCleanup}>
              Open BO Clean Up
            </Button>
          </Box>
        </Box>

        <Box className="text-headline-2" sx={{ color: INTERAX_COLORS.white, marginBottom: '16px' }}>
          Animation Testing
        </Box>
        <Box className="text-body-1" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '32px' }}>
          Test various Lottie animations and development features
        </Box>

        {/* Animation Test Cards */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Voice Lottie Test */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Voice Lottie Animation
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Test the voice animation from local JSON file
            </Typography>
            <Button variant="primary" onClick={handleVoiceLottieTest}>
              Test Voice Animation
            </Button>
          </Box>

          {/* Bubble Lottie Test */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Bubble Lottie Animation
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Test the bubble animation from Lottie.host URL
            </Typography>
            <Button variant="primary" onClick={handleBubbleLottieTest}>
              Test Bubble Animation
            </Button>
          </Box>

          {/* Voice Animation with Audio Sync */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Voice Animation + Audio Sync
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Synchronized voice animation that responds to audio levels in real-time
            </Typography>
            <Button variant="primary" onClick={handleVoiceAnimationWithAudio}>
              Test Audio Synchronization
            </Button>
          </Box>

          {/* React Recharts with Glass Effect */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              React Recharts + Glass Effect
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Beautiful React Recharts with glassmorphism effects and modern styling
            </Typography>
            <Button variant="primary" onClick={handleRechartsGlassTest}>
              Test Glass Charts
            </Button>
          </Box>

          {/* Communication Feeling Examples */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Communication Feeling Examples
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Advanced glass effects for communication with emotional experience charts and liquid glass components
            </Typography>
            <Button variant="primary" onClick={handleCommunicationFeelingExamples}>
              Test Communication Feelings
            </Button>
          </Box>

          {/* Stacked Charts - Uridium & Airtime */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Stacked Charts - Uridium & Airtime
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Interactive stacked area charts for Uridium metrics and Airtime analysis with centered and bottom-stacked
              views
            </Typography>
            <Button variant="primary" onClick={handleStackedChartsTest}>
              Test Stacked Charts
            </Button>
          </Box>

          {/* Funnel Graph Prototype */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Funnel Graph Prototype
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Renders the vanilla d3-funnel-graph example (Loop 1-3) so we can benchmark gradients, spacing, and hover
              behavior before wiring it into SessionCard.
            </Typography>
            <Button variant="primary" onClick={handleFunnelGraphTest}>
              Preview Funnel Graph
            </Button>
          </Box>

          {/* Funnel Graph Prototype - v2 (Stacked Data) */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Funnel Graph Prototype (Stacked Data)
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Feeds the same sample loops + player talk-time data used by the stacked airtime charts into the funnel
              visualization to validate data shape, tooltips, and gradients.
            </Typography>
            <Button variant="primary" onClick={handleFunnelGraphTestV2}>
              Preview Funnel Graph v2
            </Button>
          </Box>

          {/* Funnel Graph Prototype - v3 (Percentage Data) */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Funnel Graph Prototype v3 (Percentage Data)
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Uses production data structure with percentage-based talkedTotal values. Tests the new transformation
              function with proper player ordering and segment rendering.
            </Typography>
            <Button variant="primary" onClick={handleFunnelGraphTestV3}>
              Preview Funnel Graph v3
            </Button>
          </Box>

          {/* Comparison Bar Chart Playground */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Comparison Bar Chart Playground
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Experiment with the animated current vs previous visualization and missing-previous fallbacks.
            </Typography>
            <Button variant="primary" onClick={handleComparisonBarTest}>
              Test Comparison Bars
            </Button>
          </Box>

          {/* Glint Reveal Playground */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Glint Reveal Playground
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Tune the glow overlay reveal animation with configurable start/end positions, rotation, and duration.
            </Typography>
            <Button variant="primary" onClick={handleGlintRevealTest}>
              Open Glint Playground
            </Button>
          </Box>

          {/* Generate Report Visualizations */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Generate Report Visualization Playground
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Experiment with psychological safety star charts, communicative balance selections, and risk profile
              comparisons using mock data sets.
            </Typography>
            <Button variant="primary" onClick={handleGenerateReportVisuals}>
              Open Report Visualizations
            </Button>
          </Box>

          {/* Glint Duration Presets */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Glint Timing Presets
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Update the saved durations for the glint sweep, overlay fade, and full experience. These values persist in
              local storage and sync with the playground screen.
            </Typography>

            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Glint Duration (s)"
                  type="number"
                  value={glintDurationField}
                  onChange={(event) => setGlintDurationField(event.target.value)}
                  inputProps={{ min: 0, step: 0.05 }}
                  variant="outlined"
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Fade Duration (s)"
                  type="number"
                  value={fadeDurationField}
                  onChange={(event) => setFadeDurationField(event.target.value)}
                  inputProps={{ min: 0, step: 0.05 }}
                  variant="outlined"
                  fullWidth
                  size="small"
                />
              </Stack>

              <TextField
                label="Experience Duration (s)"
                type="number"
                value={experienceDurationField}
                onChange={(event) => setExperienceDurationField(event.target.value)}
                inputProps={{ min: 0, step: 0.05 }}
                variant="outlined"
                fullWidth
                size="small"
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button variant="primary" onClick={handleApplyGlintDurations}>
                  Save Durations
                </Button>
                <Button variant="secondary" onClick={handleResetGlintDurations}>
                  Reset to Defaults
                </Button>
              </Stack>
            </Stack>
          </Box>

          {/* Team Health Heart Animation */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Team Health Heart Animation
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Prototype the GSAP-powered team health heart wave animation with adjustable fill levels.
            </Typography>
            <Button variant="primary" onClick={handleTeamHealthHeartTest}>
              Test Heart Animation
            </Button>
          </Box>

          {/* Team Information Shares Line Chart */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Team Information Shares Line Chart
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Line chart visualization showing team information shares trend over periods with glassmorphism styling.
            </Typography>
            <Button variant="primary" onClick={handleTeamInformationSharesTest}>
              Test Line Chart
            </Button>
          </Box>

          {/* Personality Traits Visualization */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              Personality Traits Visualization
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Interactive slider visualization for personality traits with spotlight effects and framer-motion
              animations.
            </Typography>
            <Button variant="primary" onClick={handlePersonalityTraitsTest}>
              Test Personality Traits Chart
            </Button>
          </Box>

          {/* Dev Settings - User Profile Management */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.redErrorBackground,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.redError}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              🧹 Dev Settings - User Profile
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Clear user profile data to test the sign-up and privacy notice flow
            </Typography>
            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Button variant="secondary" onClick={handleClearUserProfile}>
                Clear Profile Data
              </Button>
              <Button variant="primary" onClick={handleResetToSignUp}>
                Go to Sign-Up Form
              </Button>
            </Box>
          </Box>

          {/* Dev Settings - Onboarding Progress */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.redErrorBackground,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.redError}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              🎮 Dev Settings - Onboarding Progress
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Clear onboarding progress to test the onboarding flow from scratch. Current steps:{' '}
              {state.user?.completedOnboardingSteps?.length || 0}
            </Typography>
            <Typography className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '12px' }}>
              Current answers: {answeredQuestionsCount} / {ASSESSMENT_TOTAL_QUESTIONS}
            </Typography>
            <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Button variant="secondary" onClick={handleClearOnboardingProgress}>
                Clear Onboarding Progress
              </Button>
            </Box>
          </Box>

          {/* Dev Settings - Reset Glow Experience Keys */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              🖱️ Reset Glow Experience Keys
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Clear the saved cursor and glow state for first-visit experiences. Reload the pages to see the glow
              effects again.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginBottom: '16px'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: INTERAX_COLORS.white
                  }}
                >
                  Deeper Team Analysis
                </Typography>
                <Button variant="secondary" size="small" onClick={handleResetDeeperTeamAnalysisExperience}>
                  Reset
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: INTERAX_COLORS.white
                  }}
                >
                  Deeper Individual Analysis
                </Typography>
                <Button variant="secondary" size="small" onClick={handleResetDeeperIndividualAnalysisExperience}>
                  Reset
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: INTERAX_COLORS.white
                  }}
                >
                  Latest Session Feedback and My Profile
                </Typography>
                <Button variant="secondary" size="small" onClick={handleResetFeedbackReadMarks}>
                  Reset
                </Button>
              </Box>
            </Box>
            <Button variant="primary" onClick={handleResetAllExperienceKeys} fullWidth>
              Reset All Experience Keys
            </Button>
          </Box>

          {/* Achievements Dev Tools */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              🏆 Achievements Dev Tools
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              View and test achievement components with different states and levels.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mt: 2
              }}
            >
              <Button
                variant="secondary"
                onClick={() => {
                  const route =
                    role === 'gamer' ? GamerRoutes.DEV_TOOLS_ACHIEVEMENTS : FacilitatorRoutes.DEV_TOOLS_ACHIEVEMENTS
                  navigate(route)
                }}
              >
                View Dev Tools Achievements Page
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  const route = role === 'gamer' ? GamerRoutes.ACHIEVEMENTS : FacilitatorRoutes.BASE + '/achievements'
                  navigate(route)
                }}
                disabled={!isAchievementsEnabled}
              >
                View User Achievements Page
              </Button>
              <Button variant="secondary" onClick={handleUnseenAchievements} disabled={!isAchievementsEnabled}>
                View Unseen Achievements Page
              </Button>
            </Box>
          </Box>

          {/* AI Coach Page */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              AI Coach
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Access the AI Coach page for reflection activities
            </Typography>
            <Button variant="primary" onClick={handleAICoach}>
              Open AI Coach
            </Button>
          </Box>

          {/* Translation Cache Management */}
          <Box
            sx={{
              padding: '24px',
              backgroundColor: INTERAX_COLORS.heritageBlue40,
              borderRadius: '12px',
              border: `1px solid ${INTERAX_COLORS.heritageBlue}`
            }}
          >
            <Typography className="text-headline-4" sx={{ color: INTERAX_COLORS.white, marginBottom: '8px' }}>
              🌐 Translation Cache
            </Typography>
            <Typography className="text-body-2" sx={{ color: INTERAX_COLORS.thunderGrey, marginBottom: '16px' }}>
              Clear cached translation data to force reload from Firebase. Useful when testing translation updates.
            </Typography>
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  const { clearTranslationCache } = await import('../../../i18n/utils/translationCache')
                  clearTranslationCache()
                  alert('Translation cache cleared! Translations will reload on next access.')
                } catch (error) {
                  console.error('Error clearing translation cache:', error)
                  alert('Error clearing translation cache. Check console for details.')
                }
              }}
            >
              Clear Translation Cache
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
